/**
 * Phone BACKLOG (DESIGN-CHANGELOG 2026-09-03; `design/GT Mobile Mine.dc.html`,
 * the `isBacklog` block). AA phase 2.
 *
 * Full-screen behind MY GAMES' first door. The sticky bar carries `◂ MY GAMES`,
 * the title with the stat line under it, a 44px `+` (TRACK A GAME — the phone's
 * one entry point; the second dashed tile the desktop keeps is gone here), then
 * the ALL / PLAYED / OWNED / WISHLIST / UNOWNED tabs as a second row inside the
 * bar. Then the full-width `⟳ PULL IN YOUR STEAM WISHLIST` button and its
 * sub-line — GOTY's AUTO-FILL, same dress (Marcus, 2026-09-15, built repo-side
 * ahead of the design) — the search box (`bkQ`), the filter rail, and a two-up
 * grid.
 *
 * THE TABS ARE AN OWNERSHIP AXIS (Marcus, 2026-09-15), not the SHELF / SCORED /
 * WATCHING strip he had removed on 2026-09-04 — that one asked about the shelf,
 * this one asks what Steam says about him. PLAYED is `steam_owned_apps`
 * playtime, his call when asked, so it does not agree with MY SCORES and is not
 * meant to. WISHLIST is a live `?op=wishlist` read cached for the session —
 * nothing persists the wishlist — and the phone shortens its label to fit five
 * cells across 332px (`bkTabs[].lS`).
 *
 * THE PHONE HAS NO HOVER, so a tile's actions are a permanent 44px row under
 * the cover: the armed ✕ on the left (`SURE?` + the 5 s drain), the bell on
 * the right in phase 4. The score badge sits on the art and IS the score
 * control, as on desktop.
 */

import { Bell } from '../components/Bell.tsx';
import { CoverArt } from '../components/CoverArt.tsx';
import { Ribbon } from '../components/Ribbon.tsx';
import { SearchBox } from '../components/SearchBox.tsx';
import { useArt, useQueueArt } from '../components/useArt.ts';
import { useEnsureMyLibrary, useEnsureWishlist } from '../components/useMyLibrary.ts';
import { useQd } from '../store/index.ts';
import { removeChip } from '../store/selectors.3c.ts';
import { backlogVM } from '../store/selectors.ts';
import type { BacklogRow } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function BacklogCard({ s: row }: { s: BacklogRow }): JSX.Element {
  const st = useQd();
  const art = useArt(row.id + 'p', row.coverCands);
  // `minWidth: 0` here and a `minmax(0, 1fr)` grid below: a `1fr` track will
  // not shrink below its content, so a long name pushed the row off the screen
  // (Marcus, 2026-09-04: "the games extend off the side of the screen").
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, background: 'var(--gt-tile)', border: '1px solid var(--gt-line)' }}>
      <div
        onClick={() => st.openGame(row.id)}
        style={{ position: 'relative', width: '100%', aspectRatio: '2/3', background: row.fall, overflow: 'hidden', cursor: 'pointer' }}
      >
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={15} ini={row.ini} hasCover={art.hasCover} cover={art.cover} />
        <Ribbon up={row.upcoming} ea={row.ea} demo={row.demo} />
        <div
          onClick={(ev) => {
            ev.stopPropagation();
            st.openScore(row.id);
          }}
          style={{
            cursor: 'pointer',
            position: 'absolute',
            top: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            background: 'var(--gt-ink)',
            borderLeft: '1px solid ' + row.scClr,
            borderBottom: '1px solid ' + row.scClr,
            padding: '7px 9px 6px',
            zIndex: 3,
          }}
        >
          <span style={{ fontFamily: PIXEL, fontSize: 15, color: row.scClr, display: 'flex', alignItems: 'center' }}>
            <span style={{ transform: 'translateY(0.13em)' }}>{row.scTxt}</span>
          </span>
          <span style={{ fontFamily: MONO, fontSize: 6.5, letterSpacing: '.12em', color: 'var(--gt-muted-2)' }}>{row.scLab}</span>
        </div>
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '10px 10px 9px',
            background: 'linear-gradient(180deg,transparent,rgba(5,5,8,.94))',
            fontWeight: 600,
            fontSize: 12.5,
            lineHeight: 1.2,
            color: '#fff',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.name}
        </div>
      </div>
      {/* the phone has no hover, so the tile's actions are a permanent 44px row */}
      <div style={{ display: 'flex', borderTop: '1px solid var(--gt-line)' }}>
        <div
          onClick={() => (row.rmArmed ? void st.soloRm(row.id) : st.bkArmSet(row.id))}
          data-arm="bk"
          style={{
            height: 44,
            flex: 'none',
            display: 'grid',
            placeItems: 'center',
            padding: '0 12px',
            borderRight: '1px solid var(--gt-line)',
            color: row.rmClr,
            fontFamily: MONO,
            fontSize: 11,
            letterSpacing: '.05em',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {row.rmArmed ? (
            <span
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '100%',
                background: 'var(--acc2)',
                opacity: 0.22,
                animation: 'gt-armdrain 5s linear forwards',
              }}
            />
          ) : null}
          <span style={{ gridArea: '1/1', position: 'relative', visibility: row.rmArmed ? 'hidden' : 'visible' }}>✕</span>
          <span style={{ gridArea: '1/1', position: 'relative', visibility: row.rmArmed ? 'visible' : 'hidden' }}>SURE?</span>
        </div>
        <span style={{ flex: 1, minWidth: 0, padding: '0 8px', fontFamily: MONO, fontSize: 8, letterSpacing: '.05em', color: row.wClr, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.w}
        </span>
        <div
          onClick={() => st.wOpen(row.id)}
          data-tip={row.bellTip}
          style={{ width: 44, height: 44, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, borderLeft: '1px solid var(--gt-line)', color: row.bellClr, cursor: 'pointer', boxSizing: 'border-box' }}
        >
          <Bell on={row.bellOn} size={12} color={row.bellClr} />
          {row.bellOn ? <span style={{ fontFamily: MONO, fontSize: 9 }}>{row.bellN}</span> : null}
        </div>
      </div>
    </div>
  );
}

export default function Backlog(): JSX.Element {
  const s = useQd();
  const v = backlogVM(s);

  // This shelf fetches its own art, the way GOTY and both Discover screens do
  // (the long note in `screens/Goty.tsx`). `revalidateVisible` covers the shelf
  // at boot and on focus now, but a game TRACKED mid-session from the local
  // catalog has been sent to `giFlush` by nobody, and for a modern appid no
  // fetched assets means no art at all. Same `queueAssets`, same per-appid
  // floor, so a shelf the boot pass already asked about costs nothing here.
  useQueueArt(v.bkRows.map((r) => r.id));

  // What the ownership tabs filter on. Fire-and-forget and self-deduping, so
  // calling it from a render is cheap after the first pass; `bkAppids` is the
  // WHOLE shelf rather than the visible rows, or a narrowed tab could never
  // learn about the games it is hiding.
  useEnsureMyLibrary(v.bkAppids);
  // WISHLISTED's own source — one Steam call per session, not per appid.
  useEnsureWishlist();

  return (
    <div
      data-screen-label="Backlog"
      style={{ display: 'flex', flexDirection: 'column', flex: 1, paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* the bar: back · title + stat · + · then the tabs ---------------------- */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: '11px 14px',
          borderBottom: '1px solid var(--gt-line)',
          background: 'var(--gt-panel)',
          position: 'sticky',
          top: 0,
          zIndex: 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            onClick={() => s.nav('mine')}
            style={{
              display: 'flex',
              gap: 7,
              alignItems: 'center',
              justifyContent: 'center',
              height: 44,
              padding: '0 11px',
              border: '1px solid var(--gt-border-2)',
              color: 'var(--gt-text-4)',
              fontFamily: MONO,
              fontSize: '9.5px',
              letterSpacing: '.05em',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flex: 'none',
              boxSizing: 'border-box',
            }}
          >
            ◂ MY GAMES
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
            <span style={{ fontFamily: PIXEL, fontSize: '9.5px', lineHeight: 1.6, color: 'var(--gt-text)', transform: 'translateY(0.13em)', whiteSpace: 'nowrap' }}>
              BACKLOG
            </span>
            <span
              style={{
                fontFamily: MONO,
                fontSize: '8.5px',
                letterSpacing: '.06em',
                color: 'var(--acc)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {v.bkSub}
            </span>
          </div>
          <div
            onClick={() => s.openTrack()}
            data-tip="TRACK A GAME"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              flex: 'none',
              border: '1px solid var(--acc)',
              background: 'var(--accSoft)',
              color: 'var(--acc)',
              fontFamily: MONO,
              fontSize: 19,
              lineHeight: 1,
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            +
          </div>
        </div>
        {/* ALL / PLAYED / OWNED / WISHLIST / UNOWNED — the segmented second row,
            in the dress the removed SHELF / SCORED / WATCHING strip used.
            9px, not the desktop's 10: five cells share ~332px on a 360px phone
            and the labels carry counts. `lS` shortens the one that still would
            not fit. */}
        <div style={{ display: 'flex', fontFamily: MONO, fontSize: 9 }}>
          {v.bkTabs.map((t) => (
            <div
              key={t.key}
              onClick={() => s.setBkTab(t.key)}
              style={{
                flex: 1,
                minWidth: 0,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid ' + t.bd,
                color: t.clr,
                background: t.bg,
                marginLeft: -1,
                position: 'relative',
                zIndex: t.z,
                cursor: 'pointer',
                boxSizing: 'border-box',
                whiteSpace: 'nowrap',
              }}
            >
              {t.lS || t.l}
            </div>
          ))}
        </div>
      </div>

      {/* the Steam wishlist import — GOTY's full-width AUTO-FILL button and its
          sub-line, same dress --------------------------------------------------- */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px 0' }}>
        <div
          onClick={() => void s.wishImport()}
          style={{
            border: '1px solid ' + v.wishBd,
            color: v.wishClr,
            fontFamily: MONO,
            fontSize: 10,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            // `wishImport`'s own guard is what actually blocks a second run; this
            // is the cursor agreeing with it.
            cursor: v.wishBusy ? 'progress' : 'pointer',
            boxSizing: 'border-box',
          }}
        >
          {v.wishLbl}
        </div>
        <div style={{ fontFamily: MONO, fontSize: '8.5px', color: 'var(--gt-muted-2)', textAlign: 'center' }}>{v.wishSub}</div>
      </div>

      {/* search, then the filter rail ------------------------------------------ */}
      {/* A block, not a flex row, so the box spans the width the way
          MONITORING's does (Marcus, 2026-09-04); the match note sits under it. */}
      <div style={{ padding: '12px 14px 0' }}>
        <SearchBox
          value={v.bkQ}
          onChange={(e) => s.setBkQ(e.target.value)}
          onClear={() => s.setBkQ('')}
          placeholder="SEARCH YOUR BACKLOG…"
          w="100%"
          ifs={12}
          pad="13px 12px"
        />
        {v.hasBkNote ? (
          <div style={{ marginTop: 8, fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-faint)' }}>{v.bkNote}</div>
        ) : null}
      </div>
      <div data-hs="1" style={{ display: 'flex', gap: 6, alignItems: 'center', overflowX: 'auto', padding: '10px 14px 0' }}>
        <div
          onClick={() => s.toggleFltOpen()}
          style={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid ' + v.fltBtnBd,
            color: v.fltBtnClr,
            background: v.fltBtnBg,
            fontFamily: MONO,
            fontSize: '10.5px',
            height: 40,
            padding: '0 13px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flex: 'none',
            boxSizing: 'border-box',
          }}
        >
          {v.fltBtnTxt}
        </div>
        {v.fltChips.map((c) => (
          <div
            key={c.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              border: '1px solid ' + c.bd,
              color: c.clr,
              background: c.bg,
              padding: '9px 11px',
              whiteSpace: 'nowrap',
              fontFamily: MONO,
              fontSize: 10,
              flex: 'none',
            }}
          >
            {c.l}
            <span onClick={() => removeChip(s, c)} style={{ cursor: 'pointer', color: 'var(--gt-text-5)', padding: 2 }}>
              ✕
            </span>
          </div>
        ))}
      </div>

      {v.bkNarrowed ? (
        <div style={{ padding: '40px 24px', textAlign: 'center', fontFamily: MONO, fontSize: 11, lineHeight: 2, color: 'var(--gt-muted)' }}>
          {v.bkNarrowTxt}
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12, padding: '14px 14px 26px' }}>
        {v.bkRows.map((row) => (
          <BacklogCard key={row.key} s={row} />
        ))}
      </div>

      {v.shelfN === 0 && !v.bkNarrowed ? (
        <div style={{ padding: '0 14px 40px', textAlign: 'center', fontFamily: MONO, fontSize: 10, letterSpacing: '.06em', lineHeight: 1.8, color: 'var(--gt-muted)' }}>
          NOTHING ON YOUR SHELF YET — THE + ABOVE TRACKS ONE
        </div>
      ) : null}
    </div>
  );
}
